import React, { useEffect, useState, useRef, createElement } from 'react';
import { CheckIcon, ArrowRightIcon } from 'lucide-react';
import { SEOHead } from './SEOHead';
import { createPricingSchema } from '../utils/structuredData';
import { useStory } from './StoryContext';
import { useNavigate } from 'react-router-dom';
export const PricingPage = () => {
  const {
    isLoggedIn
  } = useStory();
  const navigate = useNavigate();
  const stripeContainerRef = useRef<HTMLDivElement>(null);
  const [isStripeLoading, setIsStripeLoading] = useState(true);
  const [stripeError, setStripeError] = useState<string | null>(null);
  // Add Stripe script when component mounts
  useEffect(() => {
    let scriptElement: HTMLScriptElement | null = null;
    const loadStripeScript = () => {
      if (!isLoggedIn || document.querySelector('script[src*="buy-button.js"]')) {
        return;
      }
      setIsStripeLoading(true);
      setStripeError(null);
      scriptElement = document.createElement('script');
      scriptElement.src = 'https://js.stripe.com/v3/buy-button.js';
      scriptElement.async = true;
      scriptElement.crossOrigin = 'anonymous';
      // Add event handlers
      scriptElement.onload = () => {
        console.log('Stripe script loaded successfully');
        setIsStripeLoading(false);
        // Create the Stripe button after script loads
        if (stripeContainerRef.current) {
          try {
            const stripeButton = document.createElement('stripe-buy-button');
            stripeButton.setAttribute('buy-button-id', 'buy_btn_1RSPqAGXXgnFm4qoKGMhoVaX');
            stripeButton.setAttribute('publishable-key', 'pk_live_51RQxIGGXXgnFm4qoXWD8eqed14BNE4nN1HKkw6TKHRfDCqQdwQMwZN4QYMLOIS0zP3QV82rcibjnRocq0ixnJnL3003wAeol5f');
            // Clear container and append new button
            stripeContainerRef.current.innerHTML = '';
            stripeContainerRef.current.appendChild(stripeButton);
          } catch (err) {
            console.error('Error creating Stripe button:', err);
            setStripeError('Failed to initialize payment button');
          }
        }
      };
      scriptElement.onerror = error => {
        console.error('Failed to load Stripe script:', error);
        setIsStripeLoading(false);
        setStripeError('Failed to load payment system');
      };
      document.body.appendChild(scriptElement);
    };
    loadStripeScript();
    // Cleanup function
    return () => {
      if (scriptElement && document.body.contains(scriptElement)) {
        document.body.removeChild(scriptElement);
      }
    };
  }, [isLoggedIn]);
  // Add referral tracking script
  useEffect(() => {
    const initializeReferralTracking = () => {
      try {
        // Create a single script element for both scripts
        const combinedScript = document.createElement('script');
        combinedScript.type = 'text/javascript';
        combinedScript.async = true;
        // Combine both scripts into one
        combinedScript.innerHTML = `
          (function() {
            // Load PromoteKit
            var script = document.createElement('script');
            script.src = 'https://cdn.promotekit.com/promotekit.js';
            script.async = true;
            script.setAttribute('data-promotekit', '20fab87b-f678-4888-a763-ebb32cc937ff');
            script.onerror = function() { console.error('Failed to load PromoteKit script'); };
            document.head.appendChild(script);
            // Add tracking functionality
            script.onload = function() {
              setTimeout(function() {
                try {
                  document.querySelectorAll('a[href^="https://buy.stripe.com/"]').forEach(function(link) {
                    var oldBuyUrl = link.getAttribute("href");
                    var referralId = window.promotekit_referral;
                    if (oldBuyUrl && !oldBuyUrl.includes("client_reference_id")) {
                      var newBuyUrl = oldBuyUrl + "?client_reference_id=" + referralId;
                      link.setAttribute("href", newBuyUrl);
                    }
                  });
                  document.querySelectorAll("[pricing-table-id]").forEach(function(element) {
                    element.setAttribute("client-reference-id", window.promotekit_referral);
                  });
                  document.querySelectorAll("[buy-button-id]").forEach(function(element) {
                    element.setAttribute("client-reference-id", window.promotekit_referral);
                  });
                } catch (err) {
                  console.error('Error applying referral tracking:', err);
                }
              }, 1500);
            };
          })();
        `;
        // Add error handling
        combinedScript.onerror = () => {
          console.error('Failed to load referral tracking script');
        };
        document.head.appendChild(combinedScript);
        // Cleanup function
        return () => {
          try {
            if (document.head.contains(combinedScript)) {
              document.head.removeChild(combinedScript);
            }
          } catch (err) {
            console.error('Error cleaning up scripts:', err);
          }
        };
      } catch (err) {
        console.error('Error initializing referral tracking:', err);
      }
    };
    const cleanup = initializeReferralTracking();
    return () => {
      if (cleanup) cleanup();
    };
  }, []);
  const handleSubscribeClick = () => {
    if (!isLoggedIn) {
      navigate('/create-account');
    }
  };
  const pricingStructuredData = createPricingSchema();
  return <>
      <SEOHead title="Pricing Plans - Story Bunny Subscription" description="Affordable monthly plans for creating unlimited AI-powered children's stories. $7/month for 5 custom storybooks. Start your magical storytelling journey today!" canonical="https://storybunny.ai/pricing" structuredData={pricingStructuredData} data-id="element-771" />
      <div className="min-h-screen bg-[#FFFAE4] py-16 px-4" data-id="element-772">
        <div className="max-w-4xl mx-auto text-center" data-id="element-773">
          {/* Header */}
          <h1 className="text-4xl md:text-5xl font-bold text-[#6B4B3E] mb-4" data-id="element-774">
            A Magical World of Stories, Just for Your Child
          </h1>
          <p className="text-xl text-[#6B4B3E] mb-16" data-id="element-775">
            Turn imagination into stories — in seconds,
            <br data-id="element-776" />
            with safe, child-friendly AI.
          </p>
          {/* Pricing Card */}
          <div className="bg-white rounded-3xl shadow-lg p-8" data-id="element-777">
            <h2 className="text-2xl font-bold text-[#6B4B3E] mb-4" data-id="element-778">
              Story Bundle
            </h2>
            <div className="text-4xl font-bold text-[#6B4B3E] mb-8" data-id="element-779">
              $7/month
            </div>
            <div className="space-y-4 max-w-md mx-auto mb-8" data-id="element-780">
              <div className="flex items-start gap-3" data-id="element-781">
                <CheckIcon className="w-6 h-6 text-[#D4A088] flex-shrink-0 mt-1" data-id="element-782" />
                <span className="text-left text-lg text-[#6B4B3E]" data-id="element-783">
                  5 custom storybooks each month
                </span>
              </div>
              <div className="flex items-start gap-3" data-id="element-784">
                <CheckIcon className="w-6 h-6 text-[#D4A088] flex-shrink-0 mt-1" data-id="element-785" />
                <span className="text-left text-lg text-[#6B4B3E]" data-id="element-786">
                  Choose your main character, adventure type, and setting
                </span>
              </div>
              <div className="flex items-start gap-3" data-id="element-787">
                <CheckIcon className="w-6 h-6 text-[#D4A088] flex-shrink-0 mt-1" data-id="element-788" />
                <span className="text-left text-lg text-[#6B4B3E]" data-id="element-789">
                  Save your favorites in your Story Garden
                </span>
              </div>
              <div className="flex items-start gap-3" data-id="element-790">
                <CheckIcon className="w-6 h-6 text-[#D4A088] flex-shrink-0 mt-1" data-id="element-791" />
                <span className="text-left text-lg text-[#6B4B3E]" data-id="element-792">
                  Additional stories just $1 each
                </span>
              </div>
            </div>
            <div className="flex justify-center" data-id="element-793">
              {isLoggedIn ? <div ref={stripeContainerRef} className="stripe-container min-h-[50px] min-w-[200px]" data-id="element-794">
                  {isStripeLoading && <div className="text-center text-[#6B4B3E] py-2" data-id="element-795">
                      Loading payment options...
                    </div>}
                  {stripeError && <div className="text-center text-red-600 py-2" data-id="element-796">
                      {stripeError}
                    </div>}
                </div> : <button onClick={() => navigate('/create-account')} className="px-8 py-4 bg-[#D4A088] text-white rounded-full text-lg font-medium hover:bg-[#C28B73] transition-colors flex items-center justify-center" data-id="element-797">
                  <span data-id="element-798">Create Account to Subscribe</span>
                  <ArrowRightIcon className="ml-2" size={20} data-id="element-799" />
                </button>}
            </div>
          </div>
        </div>
      </div>
    </>;
};